import { consentCookieFromChange, hasTrackingConsent, readConsentCookie, readGpcOptOut, } from '#pplx/frontend/analytics/browser/consentPolicy.js';
import { getTrackingPolicyFromBackend } from '#pplx/frontend/analytics/gdprUtils.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useEffect, useState } from 'react';
const ANONYMOUS_IDENTITY = {};
// `SessionProvider` preserves both `update` and equivalent session object identities while its
// complete context value can change with internal loading state. Keying on these stable parts
// avoids treating a no-op session refresh as a new visitor.
const pendingPolicies = new WeakMap();
const acquirePolicy = (providerKey, identityKey) => {
    const pending = pendingPolicies.get(providerKey);
    if (pending?.[0] === identityKey) {
        return pending[1];
    }
    let promise;
    promise = getTrackingPolicyFromBackend()
        .catch((error) => {
        logger.warn('Visitor tracking policy lookup failed', { error });
        return 'blocking';
    })
        .finally(() => {
        if (pendingPolicies.get(providerKey)?.[1] === promise) {
            pendingPolicies.delete(providerKey);
        }
    });
    pendingPolicies.set(providerKey, [identityKey, promise]);
    return promise;
};
const resolveConsentUi = ({ isSessionLoading, trackingPolicy, consentCookie, popupDismissed, gpcOptOut, }) => {
    // GPC is a legally recognized "do not sell or share" opt-out (CPRA and friends), so it
    // short-circuits exactly like the backend's `force_blocked` policy: no consent, and no
    // banner. Deliberately no banner — the visitor has already answered the question the
    // banner asks, an "accept" click cannot lawfully override the signal, and re-asking
    // someone who opted out is the textbook shape of a dark pattern. The consent cookie is
    // still read and preserved, so an existing choice is intact if the signal goes away.
    if (isSessionLoading ||
        trackingPolicy === null ||
        trackingPolicy === 'force_blocked' ||
        gpcOptOut) {
        return {
            hasConsent: false,
            canGrantConsent: false,
            showCookieConsentPopup: false,
            hasTrackingPolicyResolved: !isSessionLoading && trackingPolicy !== null,
            consentMode: 'blocking',
        };
    }
    const consentMode = trackingPolicy === 'nonblocking' ? 'nonblocking' : 'blocking';
    const hasConsent = hasTrackingConsent(trackingPolicy, consentCookie);
    return {
        hasConsent,
        canGrantConsent: true,
        showCookieConsentPopup: !popupDismissed && consentCookie === undefined,
        hasTrackingPolicyResolved: true,
        consentMode,
    };
};
export function useGdprConsent() {
    const sessionContext = useSession();
    const providerKey = sessionContext.update;
    const identityKey = sessionContext.status === 'loading' ? null : (sessionContext.session ?? ANONYMOUS_IDENTITY);
    const isSessionLoading = identityKey === null;
    const [resolvedPolicy, setResolvedPolicy] = useState(null);
    // Withhold the previous identity's policy immediately, before the effect runs.
    const trackingPolicy = identityKey !== null && resolvedPolicy?.[0] === providerKey && resolvedPolicy[1] === identityKey
        ? resolvedPolicy[2]
        : null;
    const [consentCookie, setConsentCookie] = useState(() => readConsentCookie());
    const [popupDismissed, setPopupDismissed] = useState(false);
    const [gpcOptOut, setGpcOptOut] = useState(() => readGpcOptOut());
    useEffect(() => {
        const handleConsentChange = (event) => {
            setConsentCookie(consentCookieFromChange(event));
            setPopupDismissed(false);
        };
        window.addEventListener('consentChange', handleConsentChange);
        return () => {
            window.removeEventListener('consentChange', handleConsentChange);
        };
    }, []);
    useEffect(() => {
        if (identityKey === null) {
            setResolvedPolicy(null);
            setConsentCookie(readConsentCookie());
            setGpcOptOut(readGpcOptOut());
            setPopupDismissed(false);
            return;
        }
        setConsentCookie(readConsentCookie());
        setGpcOptOut(readGpcOptOut());
        setPopupDismissed(false);
        let isCancelled = false;
        // WHY useEffect: consent policy depends on an async per-request backend geo lookup.
        // Still fetched under GPC: `hasTrackingPolicyResolved` is what consumers wait on before
        // rendering, so skipping the lookup would strand them in their loading state forever.
        void acquirePolicy(providerKey, identityKey).then((nextTrackingPolicy) => {
            if (isCancelled) {
                return;
            }
            setResolvedPolicy([providerKey, identityKey, nextTrackingPolicy]);
        });
        return () => {
            isCancelled = true;
        };
    }, [identityKey, providerKey]);
    const closeCookieConsentPopup = () => {
        setConsentCookie(readConsentCookie());
        setPopupDismissed(true);
    };
    const { hasConsent, canGrantConsent, showCookieConsentPopup, hasTrackingPolicyResolved, consentMode, } = resolveConsentUi({
        isSessionLoading,
        trackingPolicy,
        consentCookie,
        popupDismissed,
        gpcOptOut,
    });
    return {
        hasConsent,
        canGrantConsent,
        showCookieConsentPopup,
        hasTrackingPolicyResolved,
        closeCookieConsentPopup,
        consentMode,
    };
}
//# sourceMappingURL=useGdprConsent.js.map